import { cx } from "shared/utils/classnames";
import { Badge, BadgeSize, BadgeVariant } from "shared/components/Badge";
import { Gauge } from "./components/Gauge";
import {
  CREDIT_SCORE_CONFIG,
  CREDIT_SCORE_SEGMENTS,
  CREDIT_SCORE_LABELS,
  EMPTY_SEGMENTS,
  CREDIT_SCORE_LABELS_MIN_VALUES
} from "./constants";
import type { GaugeSegment } from "./types";
import { Show } from "shared/components/helpers/Show";

const getRating = (score: number, segments: GaugeSegment[]): GaugeSegment => {
  return segments.find((s) => score >= s.min && score <= s.max) || segments[0];
};

const getBadgeVariant = (score: number): BadgeVariant => {
  if (score < CREDIT_SCORE_LABELS_MIN_VALUES[1]) return BadgeVariant.ERROR;
  if (score < CREDIT_SCORE_LABELS_MIN_VALUES[2]) return BadgeVariant.WARNING;
  return BadgeVariant.SUCCESS;
};

export const CreditScoreGauge = ({
  score,
  className
}: {
  score?: number;
  className?: string;
}) => {
  // Valid score is between 300-850; undefined, -1, or values below 300 show empty state
  const hasValidScore = score !== undefined && score >= 300 && score <= 850;
  const validScore = hasValidScore ? Math.max(300, Math.min(850, score)) : 300;
  const rating = getRating(validScore, CREDIT_SCORE_SEGMENTS);

  return (
    <div className={cx("flex flex-col items-center", className)}>
      <div className="relative w-full max-w-[340px]">
        <Gauge
          value={validScore}
          segments={hasValidScore ? CREDIT_SCORE_SEGMENTS : EMPTY_SEGMENTS}
          labels={CREDIT_SCORE_LABELS}
          config={CREDIT_SCORE_CONFIG}
          className="w-full"
          showNeedle={hasValidScore}
        />
        <Show if={hasValidScore}>
          <div className="absolute bottom-[30px] left-1/2 -translate-x-1/2 flex flex-col items-center">
            <span className="text-[40px] font-bold leading-none text-content-1000">
              {validScore}
            </span>
          </div>
        </Show>
      </div>
      <Show if={hasValidScore}>
        <Badge
          variant={getBadgeVariant(validScore)}
          size={BadgeSize.BASE}
          className="-mt-[20px]"
        >
          {rating.label}
        </Badge>
      </Show>
    </div>
  );
};
